// Diagnostic snapshot persistence — survives a hard refresh (the tab keeps its work).
//
// WHY: the whole diagnostic (session, transcript, scorecard, result, MEDDPICC) lived only in
// module-level variables in main.ts, so F5 / Ctrl+F5 wiped it and the user had to re-import and
// re-run. Model tokens were NOT the loss (the Worker's KV skill cache is keyed by
// (model, transcript hash, skill_id), so a re-run re-reads cached skill scores) — the loss was the
// rendered state, i.e. the user's time.
//
// STORAGE TIER (privacy): sessionStorage ONLY. Strictly-necessary functional storage — restores
// after a refresh or a tab-crash restore, and dies when the tab closes. Transcripts contain customer
// PII, so nothing is kept beyond the tab session: signing out, closing the tab, or exiting the app
// all leave the next sign-in fresh with no leftovers. (An earlier build offered an optional,
// consent-gated localStorage copy that survived closing the browser; that tier was retired — see
// purgeLegacyDeviceStorage(), which wipes any copy left on a returning user's device.)
//
// Nothing here is sent anywhere — this is browser-local only.

import type {
  ImportResponse,
  ManagerScorecard,
  AggregateResult,
  MeddpiccReport,
  BuyerSideResult,
} from './types.js';

export const SNAPSHOT_KEY = 'studio.diagnostic.v1';
/** Legacy: the retired opt-in-storage consent flag. Only referenced now to purge it off returning
 *  users' devices (see purgeLegacyDeviceStorage). */
export const CONSENT_KEY = 'studio.storage.consent.v1';

/** Bump when the snapshot shape changes — an older shape is discarded rather than half-restored. */
export const SNAPSHOT_VERSION = 1;

/** Guard against blowing the ~5MB per-origin quota on a very large transcript. */
export const MAX_SNAPSHOT_BYTES = 4_000_000;

export interface DiagnosticSnapshot {
  v: number;
  saved_at: number;
  transcript: string;
  session: ImportResponse;
  /** The scorecard currently in the editor (may include unsaved edits). */
  scorecard: ManagerScorecard | null;
  /** The applied scorecard that Reset restores. */
  baseline: ManagerScorecard | null;
  result: AggregateResult | null;
  meddpicc: MeddpiccReport | null;
  customizable: boolean;
  /** The Buyer-Signals result, so a refresh restores that lens too (auto-run has no manual re-run).
   *  Optional for back-compat with snapshots written before this field existed. */
  buyerSide?: BuyerSideResult | null;
  /** The imported transcript's file name, so restore shows the same Call Details "Transcript:" line
   *  (and Message Center note) the original import did. Optional for back-compat. */
  fileName?: string;
}

/** The subset of the Storage API we use — lets the unit tests pass a plain fake. */
export interface SnapshotStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

// --- pure store operations (unit-tested against a fake store) ---------------

/** Serialize + write. Returns false if the snapshot is too large or the store rejects it
 *  (quota / private mode) — callers treat persistence as best-effort and never fail a run over it. */
export function writeSnapshot(store: SnapshotStore, snap: DiagnosticSnapshot): boolean {
  let json: string;
  try {
    json = JSON.stringify(snap);
  } catch {
    return false; // non-serializable payload (shouldn't happen, all plain data)
  }
  if (json.length > MAX_SNAPSHOT_BYTES) return false;
  try {
    store.setItem(SNAPSHOT_KEY, json);
    return true;
  } catch {
    return false;
  }
}

/** Read + validate. Any malformed, foreign or older-version payload is discarded, never partly applied. */
export function readSnapshot(store: SnapshotStore): DiagnosticSnapshot | null {
  let raw: string | null;
  try {
    raw = store.getItem(SNAPSHOT_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isSnapshot(parsed)) return null;
  if (parsed.v !== SNAPSHOT_VERSION) return null;
  return parsed;
}

export function removeSnapshot(store: SnapshotStore): void {
  try {
    store.removeItem(SNAPSHOT_KEY);
  } catch {
    /* nothing we can do, and nothing depends on it succeeding */
  }
}

/** Structural check — a snapshot is only usable if the transcript and session survived intact,
 *  since everything else is re-derived from them. */
function isSnapshot(x: unknown): x is DiagnosticSnapshot {
  if (typeof x !== 'object' || x === null) return false;
  const s = x as Partial<DiagnosticSnapshot>;
  if (typeof s.v !== 'number') return false;
  if (typeof s.transcript !== 'string' || s.transcript === '') return false;
  if (typeof s.session !== 'object' || s.session === null) return false;
  if (typeof (s.session as ImportResponse).session_id !== 'string') return false;
  return true;
}

// --- browser wiring --------------------------------------------------------

function safeLocal(): SnapshotStore | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null; // storage disabled by policy / private mode
  }
}

function safeSession(): SnapshotStore | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

/** Persist the current diagnostic. Best-effort by design. Tab-scoped (sessionStorage) only. */
export function saveDiagnostic(snap: Omit<DiagnosticSnapshot, 'v' | 'saved_at'>): boolean {
  const store = safeSession();
  if (!store) return false;
  return writeSnapshot(store, { ...snap, v: SNAPSHOT_VERSION, saved_at: Date.now() });
}

/** Restore the diagnostic for this tab from sessionStorage — so a refresh keeps the user's work, but
 *  closing the tab (or signing out) starts fresh. */
export function loadDiagnostic(): DiagnosticSnapshot | null {
  const session = safeSession();
  return session ? readSnapshot(session) : null;
}

/** One-time migration for users who opted into the retired device-persistence tier: wipe any leftover
 *  localStorage snapshot + the old consent flag, so no in-progress diagnostic (customer PII) lingers
 *  on their device after this change. Best-effort; called once at startup. */
export function purgeLegacyDeviceStorage(): void {
  const local = safeLocal();
  if (!local) return;
  try {
    local.removeItem(SNAPSHOT_KEY);
    local.removeItem(CONSENT_KEY);
  } catch {
    /* ignore — nothing depends on it succeeding */
  }
}

/** Wipe every copy — used by Clear and Stop, so "cleared" means cleared on disk too. */
export function clearDiagnostic(): void {
  const local = safeLocal();
  const session = safeSession();
  if (local) removeSnapshot(local);
  if (session) removeSnapshot(session);
}

// --- last active view (route survival across a refresh) --------------------
// Which top-level view (Home / Quick Snapshot / a lens / …) the user was on. Kept in sessionStorage
// ALWAYS (never localStorage, never consent-gated): it is strictly-necessary UX state, not PII, and
// its tab-scoped lifetime is exactly what we want — a refresh returns to the same page, closing the
// tab starts fresh at the default.
export const VIEW_KEY = 'studio.view.v1';

export function saveView(view: string): void {
  const store = safeSession();
  if (!store) return;
  try {
    store.setItem(VIEW_KEY, view);
  } catch {
    /* best-effort — a lost view just falls back to the default on next load */
  }
}

export function loadView(): string | null {
  const store = safeSession();
  if (!store) return null;
  try {
    return store.getItem(VIEW_KEY);
  } catch {
    return null;
  }
}

export function clearView(): void {
  const store = safeSession();
  if (!store) return;
  try {
    store.removeItem(VIEW_KEY);
  } catch {
    /* ignore */
  }
}
